"use client"

import { FormEvent, useState, useEffect, Suspense } from 'react'
import { signIn, useSession } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { Eye, EyeOff, AlertCircle, CheckCircle, Zap } from 'lucide-react'

export default function SignInPage() {
  return (
    <Suspense fallback={null}>
      <SignInContent />
    </Suspense>
  )
}

function SignInContent() {
  const { data: session, status, update } = useSession()
  const router = useRouter()
  const search = useSearchParams()
  const callbackUrl = search.get('callbackUrl') ?? '/dashboard'
  const error = search.get('error')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [messageType, setMessageType] = useState<'error' | 'success'>('error')

  useEffect(() => {
    // Only redirect if authentication is confirmed
    if (status === 'authenticated' && session) {
      console.log('[signin] Session authenticated, checking mustChangePassword flag')

      // Check if user must change password
      const mustChangePassword = (session?.user as any)?.mustChangePassword
      console.log('[signin] mustChangePassword:', mustChangePassword)

      const redirectTo = mustChangePassword ? '/change-password' : (callbackUrl || '/dashboard')
      console.log('[signin] Redirecting to:', redirectTo)

      // Use a small delay to ensure all state is properly updated
      const redirectTimer = setTimeout(() => {
        console.log('[signin] Executing redirect to:', redirectTo)
        router.replace(redirectTo)
      }, 50)

      return () => clearTimeout(redirectTimer)
    }
  }, [status, session, router, callbackUrl])

  useEffect(() => {
    if (error) {
      const msg = error === 'CredentialsSignin'
        ? 'Invalid email or password. Please try again.'
        : 'Unable to sign in. Please try again.'
      setMessage(msg)
      setMessageType('error')
    }
  }, [error])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setMessage(null)
    setBusy(true)
    try {
      const res = await signIn('credentials', {
        email,
        password,
        redirect: false,
        callbackUrl,
      })
      console.log('[signin] signIn response:', { ok: res?.ok, status: res?.status, error: res?.error, url: res?.url })

      if (res?.ok) {
        setMessage('Signing in...')
        setMessageType('success')

        // Refresh the session to get the latest token and flags
        const updatedSession = await update()
        console.log('[signin] Session updated:', { email: updatedSession?.user?.email, hasUser: !!updatedSession?.user })

        // Wait for session to be fully established
        await new Promise(resolve => setTimeout(resolve, 150))

        // Fetch the session data directly to check flags
        const sessionResponse = await fetch('/api/auth/session')
        const currentSession = await sessionResponse.json()
        console.log('[signin] Current session:', { authenticated: !!currentSession?.user, mustChangePassword: currentSession?.user?.mustChangePassword })

        // Determine redirect destination
        const mustChangePassword = currentSession?.user?.mustChangePassword
        const redirectTo = mustChangePassword ? '/change-password' : (callbackUrl || '/dashboard')

        console.log('[signin] Performing direct redirect to:', redirectTo)

        // Use push instead of replace to allow back navigation
        // Then replace to clean up the signin page from history
        window.location.href = redirectTo
      } else {
        // Provide clearer errors when possible
        if (res?.status === 429) {
          setMessage('Too many login attempts. Please wait a few minutes and try again.')
        } else if (res?.error === 'CredentialsSignin' || res?.status === 401) {
          setMessage('Invalid email or password. Please try again.')
        } else {
          setMessage(res?.error ? String(res.error) : 'Unable to sign in. Please try again.')
        }
        setMessageType('error')
      }
    } catch (err) {
      console.error('[signin] Error during signin:', err)
      setMessage('Something went wrong. Please try again.')
      setMessageType('error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white">
      {/* Animated Background Orbs */}
      <style jsx>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        
        .blob {
          animation: blob 7s infinite;
          mix-blend-mode: multiply;
          position: absolute;
          filter: blur(40px);
        }
        
        .blob1 {
          animation-delay: 0s;
        }
        
        .blob2 {
          animation-delay: 2s;
        }
        
        .blob3 {
          animation-delay: 4s;
        }
      `}</style>

      {/* Animated Gradient Orbs */}
      <div className="blob blob1 absolute top-20 right-32 w-72 h-72 bg-gradient-to-r from-red-200 to-red-300 rounded-full opacity-40"></div>
      <div className="blob blob2 absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-red-100 to-red-200 rounded-full opacity-30"></div>
      <div className="blob blob3 absolute top-1/2 left-1/3 w-80 h-80 bg-gradient-to-r from-red-150 to-red-300 rounded-full opacity-20"></div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5" style={{
        backgroundImage: 'linear-gradient(0deg, rgba(0,0,0,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.05) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }}></div>

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8">

        {/* Login Card Container */}
        <div className="w-full flex justify-center">
          <div className="w-full max-w-md">
            {/* Login Card with Glassmorphism */}
            <div className="relative group">
              {/* Glowing Border */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-2xl blur opacity-0 group-hover:opacity-30 transition duration-300"></div>

              {/* Card */}
              <div className="relative bg-white/95 backdrop-blur-xl border border-red-200 rounded-2xl p-8 shadow-2xl group-hover:bg-white/98 transition-all duration-300">

                {/* Logo & Branding */}
                <div className="flex flex-col items-center mb-8">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center shadow-lg mb-4">
                    <img src="/logo1.png" alt="Chibiso Motors" className="w-12 h-12 object-contain" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900">Chibiso Motors</h1>
                  <p className="text-red-600 font-semibold text-sm">Management Information System</p>
                </div>

                {/* Heading */}
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">Welcome Back</h2>
                  <p className="text-slate-600">Enter your credentials to continue</p>
                </div>

                {/* Alert Messages */}
                {message && (
                  <div className={`mb-6 p-4 rounded-lg border flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300 ${
                    messageType === 'error' 
                      ? 'bg-red-500/10 border-red-500/30 text-red-300'
                      : 'bg-green-500/10 border-green-500/30 text-green-300'
                  }`}>
                    {messageType === 'error' ? (
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-sm">{message}</span>
                  </div>
                )}

                {/* Form */}
                <form className="space-y-5" onSubmit={onSubmit}>
                  {/* Email Input */}
                  <div className="group/input">
                    <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 rounded-lg focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all duration-200"
                      required
                      disabled={busy}
                      autoFocus
                    />
                  </div>

                  {/* Password Input */}
                  <div className="group/input">
                    <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 rounded-lg focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all duration-200 pr-12"
                        required
                        disabled={busy}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-red-600 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Forgot Password Link */}
                  <div className="text-right">
                    <Link
                      href="/forgot"
                      className="text-sm text-red-600 hover:text-red-700 transition-colors font-medium"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full group/btn relative overflow-hidden rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-3 px-4 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:shadow-red-600/30"
                  >
                    <span className="relative flex items-center justify-center gap-2">
                      {busy ? (
                        <>
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign In
                          <Zap className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </>
                      )}
                    </span>
                  </button>
                </form>


                {/* Security Indicators */}
                <div className="mt-6 pt-6 border-t border-slate-200 flex items-center justify-center gap-6 text-xs text-slate-600">
                  <div className="flex items-center gap-1">
                    <span>🔒</span> Secure Login
                  </div>
                  <div className="flex items-center gap-1">
                    <span>🔐</span> Encrypted
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 py-6 text-center text-sm text-slate-600 border-t border-slate-200 backdrop-blur-sm bg-white/80">
        <p>Developed and maintained by <a href="https://www.restergoliati.com" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 font-medium transition-colors">Rester Goliati</a> - AI & Software Engineering Consultant.</p>
      </div>
    </div>
  )
}
